import type { MessageView } from "@walkie/protocol"
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react"
import { api } from "../api/client"
import { agentColor, APP_SHORT } from "../lib/agents"
import { age } from "../lib/time"
import {
	focusAgent,
	jumpToMessage,
	openSettings,
	selectRoom,
	toggleGithub,
	toggleLeft,
	toggleRight,
} from "../store/actions"
import { useOpenRequests } from "../store/derived"
import { getState, setUi, useStore } from "../store/store"
import { KindBadge, StateDot, Swatch, useDismiss } from "./ui"

/**
 * `ref:owner/repo#12` and `@handle` narrow message results; the rest is text.
 * Exported for tests and the shortcut sheet.
 */
export function parseQuery(raw: string): { text: string; ref: string | null; author: string | null } {
	let ref: string | null = null
	let author: string | null = null
	const rest: string[] = []
	for (const tok of raw.trim().split(/\s+/).filter(Boolean)) {
		const r = tok.match(/^ref:(.+)$/i)
		if (r) ref = r[1]!.toLowerCase()
		else if (/^@[a-z0-9][a-z0-9-]{0,31}$/i.test(tok)) author = tok.slice(1).toLowerCase()
		else rest.push(tok)
	}
	return { text: rest.join(" "), ref, author }
}

interface Item {
	id: string
	group: string
	text: string
	render: ReactNode
	run: () => void
}

export function CommandPalette() {
	const close = () => setUi({ palette: false })
	useDismiss(close)
	const [q, setQ] = useState("")
	const [idx, setIdx] = useState(0)
	const [remote, setRemote] = useState<MessageView[] | null>(null)
	const [searching, setSearching] = useState(false)
	const rooms = useStore(s => s.rooms)
	const agents = useStore(s => s.agents)
	const messages = useStore(s => s.messages)
	const projectId = useStore(s => s.projectId)
	const now = useStore(s => s.now)
	const open = useOpenRequests()
	const list = useRef<HTMLDivElement>(null)

	const parsed = useMemo(() => parseQuery(q), [q])
	const filtered = !!(parsed.ref || parsed.author)

	useEffect(() => {
		// The server matches text, refs (exact) and author handles; narrow client-side after.
		const text = parsed.ref ?? (parsed.text || parsed.author || "")
		if (text.length < 2 || !projectId) {
			setRemote(null)
			return
		}
		setSearching(true)
		const t = setTimeout(() => {
			api
				.search(projectId, text, 50)
				.then(r => setRemote(r.messages))
				.catch(() => setRemote([]))
				.finally(() => setSearching(false))
		}, 220)
		return () => clearTimeout(t)
	}, [parsed, projectId])

	const items = useMemo<Item[]>(() => {
		// With filters, rooms/agents/requests/commands match the free text only.
		const needle = (
			parsed.ref || (parsed.author && parsed.text)
				? parsed.text
				: q.trim()
		)
			.toLowerCase()
			.replace(/^[#@]/, "")
		// A bare `ref:` filter lists messages only.
		const others = !(filtered && !needle)
		const hit = (s: string) => others && (!needle || s.toLowerCase().includes(needle))
		const out: Item[] = []
		const go = (fn: () => void) => () => {
			close()
			fn()
		}
		for (const r of Object.values(rooms)
			.filter(r => r.kind === "channel" && hit(r.name))
			.slice(0, 6))
			out.push({
				id: "r" + r.id,
				group: "Rooms",
				text: r.name,
				render: (
					<>
						<span className="dim mono">#</span>
						{r.name}
						{r.topic && <span className="dim ell">{r.topic}</span>}
					</>
				),
				run: go(() => selectRoom(r.id)),
			})
		for (const a of Object.values(agents)
			.filter(
				a =>
					hit(a.handle) ||
					hit(a.ownerHandle) ||
					hit(a.statusText ?? "") ||
					hit(a.branch ?? ""),
			)
			.slice(0, 8))
			out.push({
				id: "a" + a.id,
				group: "Agents",
				text: a.handle,
				render: (
					<>
						<Swatch agent={a} />
						<b style={{ color: agentColor(a.color) }}>@{a.handle}</b>
						<span className="dim">{a.ownerHandle}</span>
						<span className="app-badge">{APP_SHORT[a.app]}</span>
						<span className="dim ell grow">{a.statusText ?? ""}</span>
						<StateDot state={a.state} />
					</>
				),
				run: go(() => focusAgent(a.id)),
			})
		for (const m of open
			.filter(
				m =>
					(filtered
						? !parsed.text ||
							`${m.title ?? ""} ${m.body}`.toLowerCase().includes(parsed.text.toLowerCase())
						: hit(m.title ?? "") || hit(m.body)) &&
					(!parsed.author || m.author.handle.toLowerCase() === parsed.author) &&
					(!parsed.ref || m.refs.some(r => r.toLowerCase() === parsed.ref)),
			)
			.slice(0, 8))
			out.push({
				id: "q" + m.id,
				group: "Open requests",
				text: m.title ?? m.body,
				render: (
					<>
						<KindBadge kind={m.kind} />
						<span className="ell grow">{m.title ?? m.body.split("\n")[0]}</span>
						<span className="mono dim">{age(m.createdAt, now)}</span>
					</>
				),
				run: go(() => jumpToMessage(m)),
			})
		if (needle.length >= 2 || filtered) {
			const seen = new Set(out.map(i => i.id.slice(1)))
			const text = parsed.text.toLowerCase()
			// Filters: author handle and exact ref; free text must also appear.
			const keep = (m: MessageView) =>
				(!parsed.author || m.author.handle.toLowerCase() === parsed.author) &&
				(!parsed.ref || m.refs.some(r => r.toLowerCase() === parsed.ref)) &&
				(!text ||
					m.body.toLowerCase().includes(text) ||
					(m.title ?? "").toLowerCase().includes(text) ||
					m.refs.some(r => r.toLowerCase().includes(text)))
			const local = Object.values(messages)
				.filter(m => !seen.has(m.id) && !m.id.startsWith("local:") && keep(m))
				.sort((a, b) => b.seq - a.seq)
				.slice(0, filtered ? 20 : 8)
			const all = [
				...local,
				...(remote ?? []).filter(
					m => !local.some(l => l.id === m.id) && !seen.has(m.id) && keep(m),
				),
			].slice(0, filtered ? 50 : 20)
			for (const m of all)
				out.push({
					id: "m" + m.id,
					group: "Messages",
					text: m.body,
					render: (
						<>
							<span className="dim" style={{ minWidth: 70 }}>
								{m.author.handle}
							</span>
							<KindBadge kind={m.kind} />
							<span className="ell grow">
								{m.title ? `${m.title} — ` : ""}
								{m.body.split("\n")[0]}
							</span>
							<span className="dim">#{getState().rooms[m.roomId]?.name ?? "?"}</span>
						</>
					),
					run: go(() => jumpToMessage(m)),
				})
		}
		const cmds: [string, string, () => void][] = [
			["Toggle rooms rail", "[", toggleLeft],
			["Toggle agents panel", "]", toggleRight],
			["Connect Claude or Codex", "", () => setUi({ connect: true })],
			["Project settings", "", () => openSettings()],
			["Invite someone to this project", "", () => openSettings("invite")],
			["Connect a GitHub repo", "", () => openSettings("github")],
			["New project", "", () => setUi({ newProject: true })],
			["Keyboard shortcuts", "?", () => setUi({ shortcuts: true })],
			["Show blockers", "", () => setUi({ filter: "blockers" })],
			["Show delivery failures", "", () => setUi({ filter: "failing" })],
			["Show / hide GitHub events", "g h", toggleGithub],
		]
		for (const [label, key, fn] of cmds.filter(c => hit(c[0])))
			out.push({
				id: "c" + label,
				group: "Commands",
				text: label,
				render: (
					<>
						<span className="grow">{label}</span>
						{key && <kbd>{key}</kbd>}
					</>
				),
				run: go(fn),
			})
		return out
	}, [q, parsed, filtered, rooms, agents, messages, open, remote, now])

	useEffect(() => setIdx(0), [q])
	useEffect(() => {
		list.current
			?.querySelector('[aria-selected="true"]')
			?.scrollIntoView({ block: "nearest" })
	}, [idx])

	let lastGroup = ""
	return (
		<>
			<div className="scrim" onMouseDown={close} />
			<div className="palette" role="dialog" aria-label="Search and jump">
				<input
					className="palette-input"
					autoFocus
					placeholder="Jump to a room, @agent, request — or search messages (ref:owner/repo#12, @handle)"
					value={q}
					onChange={e => setQ(e.target.value)}
					role="combobox"
					aria-expanded
					aria-controls="palette-list"
					aria-activedescendant={items[idx] ? `pal-${idx}` : undefined}
					onKeyDown={e => {
						if (e.key === "ArrowDown" || (e.key === "n" && e.ctrlKey)) {
							setIdx(i => Math.min(items.length - 1, i + 1))
							e.preventDefault()
						} else if (e.key === "ArrowUp" || (e.key === "p" && e.ctrlKey)) {
							setIdx(i => Math.max(0, i - 1))
							e.preventDefault()
						} else if (e.key === "Enter") {
							items[idx]?.run()
							e.preventDefault()
						}
					}}
				/>
				<div className="palette-list" id="palette-list" role="listbox" ref={list}>
					{items.map((it, i) => {
						const head =
							it.group !== lastGroup ? <div className="pal-group">{it.group}</div> : null
						lastGroup = it.group
						return (
							<div key={it.id}>
								{head}
								<button
									id={`pal-${i}`}
									className="pal-item"
									role="option"
									aria-selected={i === idx}
									onMouseMove={() => i !== idx && setIdx(i)}
									onClick={it.run}
								>
									{it.render}
									<kbd className="pal-enter">↵</kbd>
								</button>
							</div>
						)
					})}
					{!items.length && (
						<div className="empty">{searching ? "Searching…" : "No matches"}</div>
					)}
				</div>
				<div className="pal-foot">
					<span>
						<kbd>↑</kbd> <kbd>↓</kbd> move
					</span>
					<span>
						<kbd>↵</kbd> open
					</span>
					<span>
						<kbd>esc</kbd> close
					</span>
					<span>
						<kbd>ref:</kbd> <kbd>@handle</kbd> filter
					</span>
					{searching && <span>searching messages…</span>}
				</div>
			</div>
		</>
	)
}
